/**
 * Related Items Cache System
 * 
 * A sophisticated caching system for related items and complex relationships
 * with 24-hour expiration, memory management, and performance statistics.
 * 
 * Features:
 * - 24-hour cache with automatic expiration
 * - Memory-based storage with size limits
 * - Cache statistics and refresh functionality
 * - LRU (Least Recently Used) eviction policy
 * - Compression for large datasets
 * - Cache warming and preloading
 */

import { RelatedEntityItem } from '../types';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  expires: number;
  accessCount: number;
  lastAccessed: number;
  size: number; // Estimated size in bytes
  compressed?: boolean;
}

export interface CacheStatistics {
  totalEntries: number;
  totalSize: number;
  hitRate: number;
  missRate: number;
  hits: number;
  misses: number;
  evictions: number;
  compressionRatio: number;
  oldestEntry: number;
  newestEntry: number;
  averageAccessCount: number;
}

export interface PerformanceMetrics {
  totalOperations: number;
  averageResponseTime: number;
  fastestOperation: number;
  slowestOperation: number;
  operationTypes: Record<string, {
    count: number;
    totalTime: number;
    averageTime: number;
  }>;
}

export interface CacheOptions {
  maxSize?: number; // Maximum cache size in bytes
  maxEntries?: number; // Maximum number of entries
  defaultTTL?: number; // Default time-to-live in milliseconds
  enableCompression?: boolean;
  compressionThreshold?: number; // Compress entries larger than this size
  enableStatistics?: boolean;
}

export interface CacheKey {
  type: 'related_items' | 'form_schema' | 'record_details' | 'reverse_relationships' | 'custom';
  identifier: string;
  params?: Record<string, any>;
}

// ============================================================================
// CACHE CONFIGURATION CONSTANTS
// ============================================================================

const CACHE_CONFIG = {
  DEFAULT_MAX_SIZE: 50 * 1024 * 1024, // 50MB
  DEFAULT_MAX_ENTRIES: 1000,
  DEFAULT_TTL: 24 * 60 * 60 * 1000, // 24 hours
  FORM_SCHEMA_TTL: 7 * 24 * 60 * 60 * 1000, // 7 days for schemas
  RECORD_DETAILS_TTL: 60 * 60 * 1000, // 1 hour for record details
  COMPRESSION_THRESHOLD: 10 * 1024, // 10KB
  CLEANUP_INTERVAL: 5 * 60 * 1000, // 5 minutes
  CACHE_SIZE_WARNING_THRESHOLD: 0.8 // Warn when 80% full
} as const;

// ============================================================================
// CACHE IMPLEMENTATION
// ============================================================================

class RelatedItemsCache {
  private cache = new Map<string, CacheEntry>();
  private statistics = {
    hits: 0,
    misses: 0,
    evictions: 0,
    totalAccesses: 0
  };
  private performanceMetrics = {
    operations: [] as Array<{ operation: string; duration: number; timestamp: number }>,
    operationCounts: new Map<string, { count: number; totalTime: number }>()
  };
  
  private readonly options: Required<CacheOptions>;
  private cleanupInterval?: NodeJS.Timeout;
  
  constructor(options: CacheOptions = {}) {
    this.options = {
      maxSize: options.maxSize || CACHE_CONFIG.DEFAULT_MAX_SIZE,
      maxEntries: options.maxEntries || CACHE_CONFIG.DEFAULT_MAX_ENTRIES,
      defaultTTL: options.defaultTTL || CACHE_CONFIG.DEFAULT_TTL,
      enableCompression: options.enableCompression ?? true,
      compressionThreshold: options.compressionThreshold || CACHE_CONFIG.COMPRESSION_THRESHOLD,
      enableStatistics: options.enableStatistics ?? true
    };
    
    // Start cleanup interval
    this.startCleanupInterval();
    
    console.log('[RelatedItemsCache] Initialized with options:', this.options);
  }
  
  /**
   * Generate cache key from CacheKey object
   */
  private generateKey(key: CacheKey): string {
    const base = `${key.type}:${key.identifier}`;
    if (key.params) {
      const paramString = JSON.stringify(key.params, Object.keys(key.params).sort());
      return `${base}:${this.hashString(paramString)}`;
    }
    return base;
  }
  
  /**
   * Simple string hash function for parameter serialization
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }
  
  /**
   * Estimate size of data in bytes
   */
  private estimateSize(data: any): number {
    try {
      const jsonString = JSON.stringify(data);
      return new Blob([jsonString]).size;
    } catch (error) {
      // Fallback estimation
      return JSON.stringify(data).length * 2; // Rough estimate for UTF-16
    }
  }
  
  /**
   * Compress data if enabled and above threshold
   */
  private async compressData(data: any, size: number): Promise<{ data: any; compressed: boolean }> {
    if (!this.options.enableCompression || size < this.options.compressionThreshold) {
      return { data, compressed: false };
    }
    
    try {
      // Simple compression using JSON.stringify with minimal spacing
      const compressed = JSON.stringify(data);
      return { data: compressed, compressed: true };
    } catch (error) {
      console.warn('[RelatedItemsCache] Compression failed:', error);
      return { data, compressed: false };
    }
  }
  
  /**
   * Decompress data if compressed
   */
  private decompressData(data: any, compressed: boolean): any {
    if (!compressed) return data;
    
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('[RelatedItemsCache] Decompression failed:', error);
      return data; // Return as-is if decompression fails
    }
  }
  
  /**
   * Check if entry is expired
   */
  private isExpired(entry: CacheEntry): boolean {
    return Date.now() > entry.expires;
  }
  
  /**
   * Evict least recently used entries to free up space
   */
  private evictLRU(targetSize: number): void {
    const entries = Array.from(this.cache.entries())
      .map(([key, entry]) => ({ key, entry }))
      .sort((a, b) => a.entry.lastAccessed - b.entry.lastAccessed);
    
    let currentSize = this.getCurrentSize();
    let evicted = 0;
    
    for (const { key } of entries) {
      if (currentSize <= targetSize) break;
      
      const entry = this.cache.get(key);
      if (entry) {
        currentSize -= entry.size;
        this.cache.delete(key);
        evicted++;
      }
    }
    
    if (evicted > 0) {
      this.statistics.evictions += evicted;
      console.log(`[RelatedItemsCache] Evicted ${evicted} LRU entries, freed ${this.formatBytes(this.getCurrentSize() - currentSize)}`);
    }
  }
  
  /**
   * Get current total cache size
   */
  private getCurrentSize(): number {
    return Array.from(this.cache.values()).reduce((total, entry) => total + entry.size, 0);
  }
  
  /**
   * Clean up expired entries
   */
  private cleanupExpired(): void {
    const now = Date.now();
    let cleaned = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.expires < now) {
        this.cache.delete(key);
        cleaned++;
      }
    }
    
    if (cleaned > 0) {
      console.log(`[RelatedItemsCache] Cleaned up ${cleaned} expired entries`);
    }
  }
  
  /**
   * Start periodic cleanup interval
   */
  private startCleanupInterval(): void {
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpired();
      
      // Warn if cache is getting full
      const currentSize = this.getCurrentSize();
      if (currentSize > this.options.maxSize * CACHE_CONFIG.CACHE_SIZE_WARNING_THRESHOLD) {
        console.warn(`[RelatedItemsCache] Cache is ${((currentSize / this.options.maxSize) * 100).toFixed(1)}% full`);
      }
    }, CACHE_CONFIG.CLEANUP_INTERVAL);
  }
  
  /**
   * Stop cleanup interval (for cleanup)
   */
  private stopCleanupInterval(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }
  }
  
  /**
   * Format bytes for human-readable display
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  /**
   * Set cache entry
   */
  async set<T>(key: CacheKey, data: T, ttl?: number): Promise<void> {
    const cacheKey = this.generateKey(key);
    const now = Date.now();
    const expires = now + (ttl || this.options.defaultTTL);
    const size = this.estimateSize(data);
    
    // Compress if needed
    const { data: processedData, compressed } = await this.compressData(data, size);
    
    const entry: CacheEntry<T> = {
      data: processedData,
      timestamp: now,
      expires,
      accessCount: 0,
      lastAccessed: now,
      size,
      compressed
    };
    
    // Check if we need to evict entries
    const currentSize = this.getCurrentSize();
    if (currentSize + size > this.options.maxSize || this.cache.size >= this.options.maxEntries) {
      this.evictLRU(this.options.maxSize - size);
    }
    
    this.cache.set(cacheKey, entry);
    
    console.log(`[RelatedItemsCache] Set ${key.type}:${key.identifier} (${this.formatBytes(size)}, compressed: ${compressed})`);
  }
  
  /**
   * Get cache entry
   */
  get<T>(key: CacheKey): T | null {
    const cacheKey = this.generateKey(key);
    const entry = this.cache.get(cacheKey);
    
    this.statistics.totalAccesses++;
    
    if (!entry) {
      this.statistics.misses++;
      return null;
    }
    
    if (this.isExpired(entry)) {
      this.cache.delete(cacheKey);
      this.statistics.misses++;
      return null;
    }
    
    // Update access statistics
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    this.statistics.hits++;
    
    // Decompress if needed
    const data = this.decompressData(entry.data, entry.compressed || false);
    
    return data;
  }
  
  /**
   * Check if key exists and is not expired
   */
  has(key: CacheKey): boolean {
    const cacheKey = this.generateKey(key);
    const entry = this.cache.get(cacheKey);
    
    if (!entry) return false;
    if (this.isExpired(entry)) {
      this.cache.delete(cacheKey);
      return false;
    }
    
    return true;
  }
  
  /**
   * Delete cache entry
   */
  delete(key: CacheKey): boolean {
    const cacheKey = this.generateKey(key);
    return this.cache.delete(cacheKey);
  }
  
  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
    this.statistics = {
      hits: 0,
      misses: 0,
      evictions: 0,
      totalAccesses: 0
    };
    console.log('[RelatedItemsCache] Cache cleared');
  }
  
  /**
   * Cleanup and destroy cache instance
   */
  destroy(): void {
    this.stopCleanupInterval();
    this.clear();
    console.log('[RelatedItemsCache] Cache destroyed');
  }
  
  /**
   * Get cache statistics
   */
  getStatistics(): CacheStatistics {
    const entries = Array.from(this.cache.values());
    const totalSize = this.getCurrentSize();
    const compressedEntries = entries.filter(e => e.compressed);
    
    return {
      totalEntries: this.cache.size,
      totalSize,
      hitRate: this.statistics.totalAccesses > 0 ? this.statistics.hits / this.statistics.totalAccesses : 0,
      missRate: this.statistics.totalAccesses > 0 ? this.statistics.misses / this.statistics.totalAccesses : 0,
      hits: this.statistics.hits,
      misses: this.statistics.misses,
      evictions: this.statistics.evictions,
      compressionRatio: compressedEntries.length / Math.max(entries.length, 1),
      oldestEntry: entries.length > 0 ? Math.min(...entries.map(e => e.timestamp)) : 0,
      newestEntry: entries.length > 0 ? Math.max(...entries.map(e => e.timestamp)) : 0,
      averageAccessCount: entries.length > 0 ? entries.reduce((sum, e) => sum + e.accessCount, 0) / entries.length : 0
    };
  }
  
  /**
   * Track performance metrics for operations
   */
  trackPerformance(operation: string, duration: number): void {
    const now = Date.now();
    this.performanceMetrics.operations.push({ operation, duration, timestamp: now });
    
    // Keep only last 100 operations for memory efficiency
    if (this.performanceMetrics.operations.length > 100) {
      this.performanceMetrics.operations.shift();
    }
    
    // Update operation counts
    const existing = this.performanceMetrics.operationCounts.get(operation) || { count: 0, totalTime: 0 };
    existing.count++;
    existing.totalTime += duration;
    this.performanceMetrics.operationCounts.set(operation, existing);
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): PerformanceMetrics {
    const operations = this.performanceMetrics.operations;
    
    if (operations.length === 0) {
      return {
        totalOperations: 0,
        averageResponseTime: 0,
        fastestOperation: 0,
        slowestOperation: 0,
        operationTypes: {}
      };
    }
    
    const durations = operations.map(op => op.duration);
    const operationTypes: Record<string, { count: number; totalTime: number; averageTime: number }> = {};
    
    this.performanceMetrics.operationCounts.forEach((data, operation) => {
      operationTypes[operation] = {
        count: data.count,
        totalTime: data.totalTime,
        averageTime: data.totalTime / data.count
      };
    });
    
    return {
      totalOperations: operations.length,
      averageResponseTime: durations.reduce((a, b) => a + b, 0) / durations.length,
      fastestOperation: Math.min(...durations),
      slowestOperation: Math.max(...durations),
      operationTypes
    };
  }

  /**
   * Get formatted statistics report with performance metrics
   */
  getStatisticsReport(): string {
    const stats = this.getStatistics();
    const perf = this.getPerformanceMetrics();
    const now = Date.now();
    
    let report = `
Related Items Cache Statistics
==============================
Entries: ${stats.totalEntries} / ${this.options.maxEntries}
Size: ${this.formatBytes(stats.totalSize)} / ${this.formatBytes(this.options.maxSize)}
Hit Rate: ${(stats.hitRate * 100).toFixed(1)}%
Miss Rate: ${(stats.missRate * 100).toFixed(1)}%
Hits: ${stats.hits}
Misses: ${stats.misses}
Evictions: ${stats.evictions}
Compression Ratio: ${(stats.compressionRatio * 100).toFixed(1)}%
Average Access Count: ${stats.averageAccessCount.toFixed(1)}
Oldest Entry: ${stats.oldestEntry > 0 ? Math.round((now - stats.oldestEntry) / 60000) + ' minutes ago' : 'N/A'}
Newest Entry: ${stats.newestEntry > 0 ? Math.round((now - stats.newestEntry) / 60000) + ' minutes ago' : 'N/A'}

Performance Metrics (Last ${perf.totalOperations} Operations)
========================================================`;

    if (perf.totalOperations > 0) {
      report += `
Total Operations: ${perf.totalOperations}
Average Response Time: ${perf.averageResponseTime.toFixed(2)}ms
Fastest Operation: ${perf.fastestOperation.toFixed(2)}ms
Slowest Operation: ${perf.slowestOperation.toFixed(2)}ms

Operation Breakdown:`;
      
      Object.entries(perf.operationTypes).forEach(([operation, metrics]) => {
        report += `
  ${operation}: ${metrics.count} calls, avg ${metrics.averageTime.toFixed(2)}ms`;
      });
    } else {
      report += `
No performance data available yet.`;
    }
    
    return report.trim();
  }
}

// ============================================================================
// GLOBAL CACHE INSTANCE
// ============================================================================

// Create global cache instance
const globalCache = new RelatedItemsCache({
  maxSize: CACHE_CONFIG.DEFAULT_MAX_SIZE,
  maxEntries: CACHE_CONFIG.DEFAULT_MAX_ENTRIES,
  defaultTTL: CACHE_CONFIG.DEFAULT_TTL,
  enableCompression: true,
  compressionThreshold: CACHE_CONFIG.COMPRESSION_THRESHOLD,
  enableStatistics: true
});

// ============================================================================
// CONVENIENCE FUNCTIONS FOR SPECIFIC DATA TYPES
// ============================================================================

/**
 * Cache related items for a specific record
 */
export const cacheRelatedItems = async (
  recordId: string,
  endpointName: string,
  relatedItems: Record<string, RelatedEntityItem[]>
): Promise<void> => {
  const key: CacheKey = {
    type: 'related_items',
    identifier: recordId,
    params: { endpoint: endpointName }
  };
  
  await globalCache.set(key, relatedItems);
};

/**
 * Get cached related items for a record
 */
export const getCachedRelatedItems = (
  recordId: string,
  endpointName: string
): Record<string, RelatedEntityItem[]> | null => {
  const key: CacheKey = {
    type: 'related_items',
    identifier: recordId,
    params: { endpoint: endpointName }
  };
  
  return globalCache.get(key);
};

/**
 * Generic cache function for any data type
 */
export const cacheData = async <T>(
  identifier: string,
  type: string,
  data: T
): Promise<void> => {
  const key: CacheKey = {
    type: type as any,
    identifier: identifier,
    params: {}
  };

  await globalCache.set(key, data);
};

/**
 * Generic get cached data function for any data type
 */
export const getCachedData = <T>(
  identifier: string,
  type: string
): T | null => {
  const key: CacheKey = {
    type: type as any,
    identifier: identifier,
    params: {}
  };
  
  return globalCache.get(key) as T | null;
};

/**
 * Cache form schema
 */
export const cacheFormSchema = async (formId: string, schema: any): Promise<void> => {
  await cacheData(formId, 'form_schema', schema);
};

/**
 * Get cached form schema
 */
export const getCachedFormSchema = (formId: string): any | null => {
  return getCachedData(formId, 'form_schema');
};

/**
 * Cache record details
 */
export const cacheRecordDetails = async (recordId: string, details: any): Promise<void> => {
  const key: CacheKey = {
    type: 'record_details',
    identifier: recordId
  };
  
  await globalCache.set(key, details, CACHE_CONFIG.RECORD_DETAILS_TTL);
};

/**
 * Get cached record details
 */
export const getCachedRecordDetails = (recordId: string): any | null => {
  const key: CacheKey = {
    type: 'record_details',
    identifier: recordId
  };
  
  return globalCache.get(key);
};

/**
 * Cache reverse relationships
 */
export const cacheReverseRelationships = async (
  recordId: string,
  relationships: any[]
): Promise<void> => {
  const key: CacheKey = {
    type: 'reverse_relationships',
    identifier: recordId
  };
  
  await globalCache.set(key, relationships);
};

/**
 * Get cached reverse relationships
 */
export const getCachedReverseRelationships = (recordId: string): any[] | null => {
  const key: CacheKey = {
    type: 'reverse_relationships',
    identifier: recordId
  };
  
  return globalCache.get(key);
};

// ============================================================================
// CACHE MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Get cache statistics
 */
export const getCacheStatistics = (): CacheStatistics => {
  return globalCache.getStatistics();
};

/**
 * Get formatted cache statistics report
 */
export const getCacheStatisticsReport = (): string => {
  return globalCache.getStatisticsReport();
};

/**
 * Clear all cache
 */
export const clearCache = (): void => {
  globalCache.clear();
};

/**
 * Clear cache entries for specific type
 */
export const clearCacheByType = (type: CacheKey['type']): void => {
  // Note: This is a simplified implementation
  // A more sophisticated version would track keys by type
  console.warn('[RelatedItemsCache] clearCacheByType not fully implemented - use clearCache() instead');
  globalCache.clear();
};

/**
 * Warm up cache with commonly accessed data
 */
export const warmUpCache = async (
  recordIds: string[],
  endpointName: string,
  dataFetcher: (recordId: string) => Promise<any>
): Promise<void> => {
  console.log(`[RelatedItemsCache] Warming up cache for ${recordIds.length} records`);
  
  const promises = recordIds.map(async (recordId) => {
    try {
      const data = await dataFetcher(recordId);
      await cacheRelatedItems(recordId, endpointName, data);
    } catch (error) {
      console.warn(`[RelatedItemsCache] Failed to warm up cache for ${recordId}:`, error);
    }
  });
  
  await Promise.all(promises);
  console.log(`[RelatedItemsCache] Cache warm-up completed`);
};

/**
 * Cleanup function for application shutdown
 */
export const destroyCache = (): void => {
  globalCache.destroy();
};

// ============================================================================
// EXPORT DEFAULT CACHE INSTANCE
// ============================================================================

export default globalCache;

// Export cache class for custom instances
export { RelatedItemsCache };